// Phase 20 · Community V3 · Q&A panel (submit + vote + answer)
import React, { useState, useEffect, useCallback } from "react";
import { ArrowUp, ArrowDown, Check, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { isAdminRole } from "@/data/v12_user_nav";

const API = process.env.REACT_APP_BACKEND_URL;

function QuestionRow({ q, viewer, isHost, onVote, onAnswer, onApprove }) {
    const [answerBody, setAnswerBody] = useState("");
    const [replying, setReplying] = useState(false);
    const isPending = q.status === "pending";

    return (
        <div className={`rounded-xl p-3 border ${isPending && !isHost ? "hidden" : ""} ${isPending ? "border-neutral-600 bg-neutral-800" : q.is_answered ? "border-[#CCCCCC]/40 bg-[#CCCCCC]/5" : "border-[#1A1A1A] bg-[#111111]"}`} data-testid={`qa-row-${q.id}`}>
            <div className="flex gap-3">
                <div className="flex flex-col items-center gap-0.5">
                    <button
                        type="button"
                        onClick={() => onVote(q.id, "up")}
                        disabled={!viewer}
                        className="text-neutral-400 hover:text-[#CCCCCC] disabled:opacity-40"
                        data-testid={`qa-up-${q.id}`}
                    >
                        <ArrowUp size={14} />
                    </button>
                    <p className="text-xs font-bold text-neutral-200 tabular-nums" data-testid={`qa-score-${q.id}`}>
                        {q.score || 0}
                    </p>
                    <button
                        type="button"
                        onClick={() => onVote(q.id, "down")}
                        disabled={!viewer}
                        className="text-neutral-400 hover:text-[#FAFAFA] disabled:opacity-40"
                    >
                        <ArrowDown size={14} />
                    </button>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[11px] text-neutral-500">@{q.asker_username}</span>
                        {isPending && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-800 text-neutral-200 font-semibold uppercase tracking-widest">
                                Pending
                            </span>
                        )}
                        {q.is_answered && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#CCCCCC]/10 text-[#CCCCCC] font-semibold uppercase tracking-widest">
                                <Check size={9} /> Answered
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-neutral-100 whitespace-pre-wrap">{q.question_body}</p>
                    {q.is_answered && q.answer_body && (
                        <div className="mt-2 pl-3 border-l-2 border-[#CCCCCC]/40 text-sm text-neutral-300 whitespace-pre-wrap">
                            <p className="text-[10px] text-[#CCCCCC] uppercase tracking-widest font-semibold mb-1">Host answer</p>
                            {q.answer_body}
                        </div>
                    )}
                    {isHost && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            {isPending && (
                                <button
                                    type="button"
                                    onClick={() => onApprove(q.id)}
                                    className="text-[11px] px-2 py-1 rounded-lg bg-[#FFFFFF]/10 border border-[#FFFFFF]/40 text-[#FFFFFF] hover:bg-[#FFFFFF]/20"
                                    data-testid={`qa-approve-${q.id}`}
                                >
                                    Approve
                                </button>
                            )}
                            {!q.is_answered && !isPending && (
                                <button
                                    type="button"
                                    onClick={() => setReplying((v) => !v)}
                                    className="text-[11px] px-2 py-1 rounded-lg bg-[#CCCCCC]/10 border border-[#CCCCCC]/40 text-[#CCCCCC] hover:bg-[#CCCCCC]/20"
                                    data-testid={`qa-answer-toggle-${q.id}`}
                                >
                                    {replying ? "Cancel" : "Answer"}
                                </button>
                            )}
                        </div>
                    )}
                    {replying && (
                        <div className="mt-2 flex items-center gap-2">
                            <input
                                type="text"
                                value={answerBody}
                                onChange={(e) => setAnswerBody(e.target.value)}
                                placeholder="Your answer…"
                                maxLength={5000}
                                className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-2 py-1.5 text-sm text-neutral-100 focus:outline-none focus:border-[#CCCCCC]"
                                data-testid={`qa-answer-input-${q.id}`}
                            />
                            <button
                                type="button"
                                onClick={() => { onAnswer(q.id, answerBody); setAnswerBody(""); setReplying(false); }}
                                disabled={!answerBody.trim()}
                                className="px-2 py-1.5 rounded-lg bg-[#CCCCCC] text-neutral-900 font-semibold text-xs disabled:opacity-40"
                                data-testid={`qa-answer-submit-${q.id}`}
                            >
                                <Send size={14} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function EventQAPanel({ event, viewer }) {
    const [items, setItems] = useState([]);
    const [pendingItems, setPendingItems] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const isHost = event.is_host || isAdminRole(viewer?.role);

    const load = useCallback(async () => {
        try {
            const r = await fetch(`${API}/api/community/events/${event.id}/questions?status=approved&sort=score_desc&limit=50`);
            if (r.ok) { const d = await r.json(); setItems(d.items || []); }
            if (isHost) {
                const p = await fetch(`${API}/api/community/events/${event.id}/questions?status=pending&limit=50`, { credentials: "include" });
                if (p.ok) { const d = await p.json(); setPendingItems(d.items || []); }
            }
        } catch {}
    }, [event.id, isHost]);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!text.trim() || text.trim().length < 10) {
            toast.error("Question must be 10–500 chars");
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/events/${event.id}/questions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ question_body: text.trim() }),
            });
            if (!r.ok) {
                toast.error(r.status === 429 ? "Slow down" : "Submit failed");
                return;
            }
            setText("");
            toast.success("Question submitted (host will approve)");
            load();
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    const vote = async (qid, dir) => {
        try {
            const r = await fetch(`${API}/api/community/questions/${qid}/vote`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ direction: dir }),
            });
            if (r.ok) load();
            else toast.error("Vote failed");
        } catch {}
    };

    const approve = async (qid) => {
        try {
            const r = await fetch(`${API}/api/community/questions/${qid}/approve`, {
                method: "POST", credentials: "include",
            });
            if (r.ok) { toast.success("Approved"); load(); }
        } catch {}
    };

    const answer = async (qid, body) => {
        try {
            const r = await fetch(`${API}/api/community/questions/${qid}/answer`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ answer_body: body }),
            });
            if (r.ok) { toast.success("Answered"); load(); }
            else toast.error("Answer failed");
        } catch {}
    };

    return (
        <section className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-4" data-testid="event-qa-panel">
            <div className="flex items-center gap-2 mb-3">
                <MessageCircle size={16} className="text-[#FFFFFF]" />
                <h3 className="text-sm font-semibold text-neutral-100">Q&A</h3>
                <span className="text-[10px] text-neutral-500 ml-auto uppercase tracking-widest">
                    {items.length} approved{isHost && pendingItems.length ? ` · ${pendingItems.length} pending` : ""}
                </span>
            </div>
            {viewer && (
                <div className="flex items-center gap-2 mb-3">
                    <input
                        type="text"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Ask a question (10–500 chars)"
                        maxLength={500}
                        className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="qa-input"
                    />
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-3 py-2 bg-white text-neutral-900 font-semibold rounded-lg text-xs disabled:opacity-40"
                        data-testid="qa-submit"
                    >
                        Ask
                    </button>
                </div>
            )}
            <div className="space-y-2">
                {isHost && pendingItems.map((q) => (
                    <QuestionRow key={q.id} q={q} viewer={viewer} isHost={isHost} onVote={vote} onAnswer={answer} onApprove={approve} />
                ))}
                {!items.length && !pendingItems.length && (
                    <p className="text-xs text-neutral-500 italic text-center py-4">No questions yet</p>
                )}
                {items.map((q) => (
                    <QuestionRow key={q.id} q={q} viewer={viewer} isHost={isHost} onVote={vote} onAnswer={answer} onApprove={approve} />
                ))}
            </div>
        </section>
    );
}
